import * as t from './types'
import * as effects from 'redux-saga/effects'
import * as api from './api'
import { pipe } from 'fp-ts/lib/function'
import { CancelToken } from 'axios'
import {
  takeLatest,
  matchPathGuard,
  waitForNymID,
  waitForOrganization,
} from '../../saga'
import { task, either, array, option } from 'fp-ts'
import { LocationChangeAction } from 'connected-react-router'
import { lens } from 'lens.ts'
import { State } from '../../store'
import { getBalanceAction, hasBalance } from '../Balance'
import {
  Organization,
  foldEitherToAction,
  memoed,
  Loading,
  NotRequested,
} from '@library/react-toolkit'

export const initialState: t.State = {
  collectFundsState: { selectedAsset: option.none, result: NotRequested },
  query: { page: 0, itemsPerPage: 10 },
  transactions: memoed.init({ data: [], count: 0 }),
}

const _l = lens<t.State>()

export const reducer = (s: t.State = initialState, a: t.Actions): t.State => {
  switch (a.type) {
    case 'WALLET/get_transactions':
      return pipe(
        s,
        _l.query.set(q => ({ ...q, ...a.payload })),
        _l.transactions.set(memoed.step<t.TransactionData>(Loading))
      )
    case 'WALLET/get_transactions_result':
      return pipe(s, _l.transactions.set(memoed.step(a.payload)))
    case 'WALLET/start_collection_action':
      return pipe(
        s,
        _l.collectFundsState.selectedAsset.set(option.some(a.payload))
      )
    case 'WALLET/end_collection_action':
      return pipe(s, _l.collectFundsState.selectedAsset.set(option.none))
    case 'WALLET/collect_funds':
      return pipe(s, _l.collectFundsState.result.set(Loading))
    case 'WALLET/collect_funds_result':
      return pipe(
        s,
        _l.collectFundsState.result.set(a.payload),
        _l.collectFundsState.selectedAsset.set(x =>
          a.payload._tag === 'Ok' ? option.none : x
        )
      )
    default:
      return s
  }
}

function* getTransactionsSaga(_: t.GetTransactionsAction, token?: CancelToken) {
  const q: t.State['query'] = yield effects.select((s: State) => s.wallet.query)
  const userNymID: string = yield waitForNymID()
  const organization: Organization = yield waitForOrganization()

  // @ts-ignore
  yield yield pipe(
    api.getTransactions(organization.name, q, userNymID, token),
    task.map(foldEitherToAction(t.getTransactionsResult)),
    task.map(a => effects.put(a)),
    e => effects.call(e)
  )
}

function* collectFundsSaga(a: t.CollectFundsAction, token?: CancelToken) {
  const organization: Organization = yield waitForOrganization()

  // @ts-ignore
  yield yield pipe(
    api.collectFunds(organization.name, a.payload, token),
    task.map(
      either.fold(
        e => [t.collectFundsResult.err(e)],
        () => [
          t.collectFundsResult.ok(undefined),
          getBalanceAction(),
          t.getTransactionsAction({}),
        ]
      )
    ),
    task.map(array.map(action => effects.put(action))),
    task.map(es => effects.all(es)),
    e => effects.call(e)
  )
}

function* changePageSaga(_: LocationChangeAction) {
  yield waitForOrganization()

  // @ts-ignore
  if (!(yield hasBalance())) {
    yield effects.all([
      effects.put(t.getTransactionsAction({})),
      effects.put(getBalanceAction()),
    ])
  } else {
    yield effects.put(t.getTransactionsAction({}))
  }
}

export function* saga() {
  yield effects.all([
    takeLatest('WALLET/get_transactions', getTransactionsSaga),
    effects.takeLeading('WALLET/collect_funds', collectFundsSaga),
    takeLatest(matchPathGuard('/wallet'), changePageSaga),
  ])
}
